import { useState } from 'react';
import {
  Alert, Box, Button, LinearProgress, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Typography, useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { MINIMO_PARA_CREDITO, modificadorDeCredito } from '@film/shared';
import { api } from '../lib/api.js';
import type {
  ApiFallo, EstadoDescarga, FilePart, PartState, TransferDetail, TransferSource,
} from '../lib/api.js';
import { formatearDuracion, formatearHace, formatearTamano, formatearVelocidad } from '../lib/eventos.js';

/** Cada parte de un fichero eD2k son ~9,28 MiB. Es constante del protocolo, no un ajuste. */
const BYTES_POR_PARTE = 9_728_000;

/** Tope de columnas del mapa. Un fichero de 6 GB son 655 partes: una caja por parte son 655
 *  nodos por cada descarga abierta. Cada columna pinta sus partes con un degradado de cortes
 *  duros, así que agrupar no pierde ninguna — ver `MapaPartes`. */
const MAX_COLUMNAS = 160;

/** Los mismos colores semánticos que el resto de la aplicación: verde «lo tienes», naranja
 *  «en marcha», rojo «mal». El `primary` de la marca queda fuera a propósito —es ámbar,
 *  igual que el naranja— y en una columna de tres píxeles no se distinguirían. */
function colorDe(t: Theme, e: PartState): string {
  switch (e) {
    case 'complete': return t.palette.success.main;
    case 'transferring': return t.palette.warning.main;
    case 'corrupt': return t.palette.error.main;
    // Falta y no la tiene nadie conectado: es lo que deja una descarga clavada, así que se
    // ve, pero no se pinta de rojo — no está rota, está esperando a que aparezca alguien.
    case 'missing': return t.palette.text.disabled;
    // Un estado que aMule ha mandado y no está en nuestra tabla. Morado porque no se parece
    // a ningún otro: la señal de que hay que ir a mirar, no de que pase algo malo.
    case 'unknown': return t.palette.secondary.main;
    case 'incomplete': return t.palette.action.disabledBackground;
  }
}

const NOMBRE: Record<PartState, string> = {
  complete: 'completa',
  incomplete: 'falta',
  missing: 'falta y no la tiene nadie',
  transferring: 'bajando',
  corrupt: 'corrupta',
  unknown: 'estado que aMule no nos ha explicado',
};

/** Orden fijo para la leyenda, de lo bueno a lo raro. */
const ORDEN: PartState[] = ['complete', 'transferring', 'incomplete', 'missing', 'corrupt', 'unknown'];

const PRIORIDAD: Record<TransferDetail['priority'], string> = {
  low: 'baja',
  normal: 'normal',
  high: 'alta',
  auto: 'automática',
};

function contar(partes: FilePart[]): Map<PartState, number> {
  const m = new Map<PartState, number>();
  for (const p of partes) m.set(p.state, (m.get(p.state) ?? 0) + 1);
  return m;
}

/** El mapa de partes del fichero.
 *
 *  Cada columna agrupa varias partes y las pinta **todas**, con un degradado de cortes
 *  duros. La primera versión daba a la columna un solo color, el de la mayoría, y eso mentía
 *  a lo grande: eD2k baja las partes salteadas, así que con 85 de 633 completas casi ningún
 *  grupo de cuatro lo estaba entero y el fichero se veía sin empezar. Con el degradado,
 *  agrupar solo pierde resolución —el navegador mezcla lo que no llega a un píxel—, que es
 *  justo lo que se quiere: la columna se ve a medias porque lo está. */
function MapaPartes({ partes }: { partes: FilePart[] }) {
  const t = useTheme();
  // El `max(1, …)` no es paranoia: con la lista vacía el paso sería 0 y el bucle no
  // terminaría nunca. Cuelga la pestaña, y aMule responde sin partes mientras reserva.
  const porColumna = Math.max(1, Math.ceil(partes.length / MAX_COLUMNAS));

  const columnas = [];
  for (let i = 0; i < partes.length; i += porColumna) {
    const grupo = partes.slice(i, i + porColumna);
    const paso = 100 / grupo.length;
    columnas.push({
      desde: i + 1,
      hasta: i + grupo.length,
      cuenta: contar(grupo),
      fondo: `linear-gradient(to right, ${grupo
        .map((p, n) => `${colorDe(t, p.state)} ${n * paso}% ${(n + 1) * paso}%`)
        .join(', ')})`,
    });
  }

  return (
    <Box sx={{ display: 'flex', height: 20, borderRadius: 0.5, overflow: 'hidden' }}>
      {columnas.map((c) => (
        <Tooltip
          key={c.desde}
          title={`${c.desde === c.hasta ? `Parte ${c.desde}` : `Partes ${c.desde}–${c.hasta}`} · ${ORDEN
            .filter((e) => c.cuenta.has(e))
            .map((e) => `${c.cuenta.get(e)} ${NOMBRE[e]}`)
            .join(', ')}`}
        >
          <Box sx={{ flex: 1, minWidth: 2, backgroundImage: c.fondo }} />
        </Tooltip>
      ))}
    </Box>
  );
}

/** La leyenda enseña **lo que hay en este fichero**, no la lista entera de estados posibles.
 *  Un «corrupta» permanente en la leyenda de una descarga impecable es ruido, y un morado
 *  que aparece de pronto se lee como lo que es: algo nuevo. */
function Leyenda({ cuenta }: { cuenta: Map<PartState, number> }) {
  const t = useTheme();
  return (
    <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
      {ORDEN.filter((e) => cuenta.has(e)).map((e) => (
        <Stack key={e} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: colorDe(t, e) }} />
          <Typography variant="caption" color="text.secondary">
            {NOMBRE[e]} ({cuenta.get(e)})
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function Dato({ k, v, nota }: { k: string; v: string; nota?: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {k}
      </Typography>
      <Typography variant="body2">{v}</Typography>
      {nota && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {nota}
        </Typography>
      )}
    </Box>
  );
}

/** Lo que hace ahora mismo esa fuente. Lo que no esté aquí se enseña crudo: es preferible un
 *  `askingforparts` sin traducir a inventarle un significado. */
const ESTADO_FUENTE: Record<string, string> = {
  downloading: 'mandando datos',
  onqueue: 'en su cola',
  connecting: 'conectando',
  connected: 'conectada',
  requesting: 'pidiendo partes',
  nonewedfiles: 'ya no tiene el fichero',
  toomanyconns: 'con demasiadas conexiones',
  // Los dos con ID baja: ninguno puede abrir conexión hacia el otro. No se arregla
  // esperando, y explicarlo evita buscar el fallo en otro sitio.
  lowtolowip: 'los dos con ID baja: no hay conexión posible',
  unknown: 'sin determinar',
};

const ORIGEN: Record<string, string> = {
  server: 'servidor',
  kad: 'Kad',
  sourceexchange: 'otra fuente',
  passive: 'nos encontró ella',
  unknown: '—',
};

/** El código ISO a su bandera. Donde no haya emoji de banderas se verán las dos letras,
 *  que siguen diciendo lo mismo. */
export function bandera(iso: string): string {
  if (iso.length !== 2) return '';
  return String.fromCodePoint(...[...iso.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function TablaFuentes({ fuentes }: { fuentes: TransferSource[] }) {
  // Primero las que dan datos, luego las que esperan, por puesto: el orden en que se miran
  // cuando la pregunta es «¿por qué no baja?».
  const ordenadas = [...fuentes].sort(
    (a, b) =>
      b.downloadSpeedBps - a.downloadSpeedBps ||
      (a.remoteQueueRank || Infinity) - (b.remoteQueueRank || Infinity),
  );

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 720 }}>
        <TableHead>
          <TableRow>
            <TableCell>Fuente</TableCell>
            <TableCell>Qué hace</TableCell>
            <TableCell align="right">Velocidad</TableCell>
            <TableCell align="right">Tiene</TableCell>
            <TableCell align="right">Nos ha dado</TableCell>
            <TableCell align="right">
              <Tooltip title="Por cuánto multiplica esa fuente nuestro tiempo de espera en su cola, según lo que le hemos subido. Es el crédito de eD2k: por cliente, no global.">
                <span>Crédito</span>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ordenadas.map((f) => (
            <TableRow key={f.id}>
              <TableCell>
                <Typography variant="body2">
                  {bandera(f.countryCode)} {f.name || f.ip || 'sin nombre'}
                  {f.a4af && (
                    <Tooltip title="La tenemos, pero está ocupada dándonos otro fichero (A4AF)">
                      <Box component="span" sx={{ color: 'text.disabled' }}> · A4AF</Box>
                    </Tooltip>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    f.software === 'unknown' ? null : `${f.software} ${f.softwareVersion}`.trim(),
                    ORIGEN[f.origin] ?? f.origin,
                    f.identified ? null : 'sin identificar',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {ESTADO_FUENTE[f.downloadState] ?? f.downloadState}
                </Typography>
                {f.remoteQueueRank > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    puesto {f.remoteQueueRank}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">{formatearVelocidad(f.downloadSpeedBps)}</TableCell>
              <TableCell align="right">
                <Tooltip title={`${f.availableParts} partes`}>
                  <Box component="span">{f.partProgressPct.toFixed(0)}%</Box>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                {f.totalBytes > 0 ? formatearTamano(f.totalBytes) : '—'}
              </TableCell>
              <TableCell align="right">
                <Credito subido={f.uploadedTotalBytes} bajado={f.totalBytes} identificada={f.identified} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/** El crédito que se estima que esa fuente nos aplica, con lo que le hemos subido debajo.
 *  Sin identificación segura aMule no da crédito a nadie, así que ahí se dice que no cuenta
 *  en vez de enseñar un multiplicador que esa fuente no va a aplicar. */
function Credito({ subido, bajado, identificada }: { subido: number; bajado: number; identificada: boolean }) {
  const m = modificadorDeCredito(subido, bajado);
  if (subido < MINIMO_PARA_CREDITO) {
    return (
      <Tooltip title={`Le has subido ${formatearTamano(subido)}; el crédito empieza en 1 MB`}>
        <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
      </Tooltip>
    );
  }
  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: identificada ? 'text.primary' : 'text.disabled' }}
      >
        ×{m.toFixed(1)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {identificada ? `${formatearTamano(subido)} subidos` : 'sin identificar: no lo aplica'}
      </Typography>
    </Box>
  );
}

/** Las fuentes una a una. Se pide solo al desplegarla: la lista cambia sin parar y a nadie
 *  le hace falta si no la está mirando. */
function Fuentes({ downloadId, viva }: { downloadId: string; viva: boolean }) {
  const [abierta, setAbierta] = useState(false);
  const { data, error, isPending } = useQuery({
    queryKey: ['fuentes-amule', downloadId],
    queryFn: () => api.fuentesAmule(downloadId),
    enabled: abierta,
    refetchInterval: viva ? 5_000 : false,
  });

  if (!abierta) {
    return (
      <Button size="small" onClick={() => setAbierta(true)} sx={{ alignSelf: 'flex-start' }}>
        Ver las fuentes una a una
      </Button>
    );
  }
  if (isPending) return <LinearProgress />;
  if (error) {
    return <Alert severity="warning">No se pudieron leer las fuentes: {(error as ApiFallo).message}</Alert>;
  }
  if (!data.fuentes.length) {
    return (
      <Alert severity="info">
        aMule no tiene ninguna fuente conectada para este fichero ahora mismo.
      </Alert>
    );
  }
  return <TablaFuentes fuentes={data.fuentes} />;
}

function Contenido({ d, downloadId, viva }: { d: TransferDetail; downloadId: string; viva: boolean }) {
  const cuenta = contar(d.parts);
  const completas = cuenta.get('complete') ?? 0;
  const sinFuente = cuenta.get('missing') ?? 0;
  const pendientes = d.parts.filter((p) => p.state !== 'complete');
  // La parte más rara de las que faltan: si nadie conectado la tiene, la descarga no
  // termina por mucho que las demás vuelen, y eso no se ve en el porcentaje.
  const masRara = pendientes.length ? Math.min(...pendientes.map((p) => p.sources)) : null;
  const ahorro = d.sizeDone - d.sizeXfer;
  const bajando = d.status === 'downloading';

  return (
    <Stack spacing={1.5}>
      {d.parts.length > 0 && (
        <Box>
          <MapaPartes partes={d.parts} />
          <Leyenda cuenta={cuenta} />
        </Box>
      )}

      {/* Que nadie tenga una parte es LA causa de que una descarga se quede al 98%, y no
          se deduce de ningún porcentaje. Vale la pena decirlo con todas las letras. */}
      {bajando && sinFuente > 0 && (
        <Alert severity="warning">
          {sinFuente === 1
            ? 'Una de las partes que faltan no la tiene ninguna de las fuentes conectadas.'
            : `${sinFuente} de las partes que faltan no las tiene ninguna de las fuentes conectadas.`}{' '}
          La descarga puede quedarse parada ahí hasta que aparezca alguien que las tenga.
        </Alert>
      )}
      {bajando && d.lastSeenComplete === null && (
        <Alert severity="info">
          Nunca se ha visto una copia entera de este fichero entre las fuentes: se está
          reconstruyendo a trozos, y no hay garantía de que estén todos.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
        <Dato
          k="Partes"
          v={`${completas} de ${d.partCount} completas`}
          nota={`${formatearTamano(BYTES_POR_PARTE)} cada una`}
        />
        <Dato
          k="Disponible entre las fuentes"
          v={`${d.availablePartCount} de ${d.partCount} partes`}
          nota="Lo que se podría conseguir ahora mismo. No es lo tuyo: sin fuentes conectadas marca 0 aunque el fichero esté entero."
        />
        <Dato
          k="Fuentes"
          v={`${d.sources.transferring} transmitiendo de ${d.sources.total}`}
          nota={[
            d.sources.notCurrent ? `${d.sources.notCurrent} localizadas pero sin conexión ahora` : null,
            d.sources.a4af ? `${d.sources.a4af} ocupadas con otro fichero tuyo (A4AF)` : null,
            masRara !== null ? `la parte que falta más rara la tienen ${masRara}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        <Dato
          k="Descargando de verdad"
          v={formatearDuracion(d.downloadActiveTime)}
          nota="Sin contar lo que lleva parada, en pausa o buscando fuentes."
        />
        <Dato k="Último byte recibido" v={d.lastChanged ? formatearHace(d.lastChanged) : '—'} />
        <Dato
          k="Copia completa vista por última vez"
          v={formatearHace(d.lastSeenComplete)}
          nota="Entre las fuentes, no en tu disco."
        />
        <Dato
          k="Traído de la red"
          v={formatearTamano(d.sizeXfer)}
          nota={
            ahorro > 0
              ? `${formatearTamano(d.sizeDone)} en disco: ${formatearTamano(ahorro)} los puso la compresión del protocolo`
              : `${formatearTamano(d.sizeDone)} en disco`
          }
        />
        <Dato
          k="Datos descartados"
          v={d.lostToCorruption > 0 ? formatearTamano(d.lostToCorruption) : 'ninguno'}
          nota={
            d.savedByIch > 0
              ? `${formatearTamano(d.savedByIch)} se recuperaron sin volver a bajar la parte entera (ICH)`
              : 'Lo que llegó corrupto y hubo que volver a pedir.'
          }
        />
        <Dato
          k="Prioridad en aMule"
          v={PRIORIDAD[d.priority]}
          nota={d.priorityAuto ? 'la ajusta aMule solo' : 'fijada a mano'}
        />
        <Dato
          k="Te lo piden"
          v={d.queuedCount === 1 ? '1 cliente' : `${d.queuedCount} clientes`}
          nota="Esperando en tu cola de subida por este mismo fichero."
        />
        {d.status === 'hashing' && (
          <Dato
            k="Rehasheando"
            v={`${d.hashingProgress} de ${d.partCount} partes`}
            nota="aMule está comprobando el fichero parcial; no es una descarga parada."
          />
        )}
        <Dato k="Fichero parcial en" v={d.path || '—'} />
      </Box>

      <Fuentes downloadId={downloadId} viva={viva} />
    </Stack>
  );
}

/** Lo que aMule sabe de una descarga y no cabe en la lista.
 *
 *  Se monta solo cuando el detalle está abierto, y solo entonces se pregunta: el mapa de
 *  partes son cientos de entradas por fichero y no tiene sentido traerlo para catorce
 *  descargas plegadas. Mientras la descarga se mueve se refresca cada pocos segundos;
 *  parada, una vez y basta. */
export function DetalleAmule({
  downloadId,
  viva,
  estado,
}: {
  downloadId: string;
  viva: boolean;
  estado: EstadoDescarga;
}) {
  const { data, error, isPending } = useQuery({
    queryKey: ['detalle-amule', downloadId],
    queryFn: () => api.detalleAmule(downloadId),
    refetchInterval: viva ? 4_000 : false,
  });

  if (isPending) return <LinearProgress sx={{ my: 1 }} />;
  if (error) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        No se pudo preguntar a aMule: {(error as ApiFallo).message}
      </Alert>
    );
  }
  if (!data?.detalle) {
    // Todo lo que sabemos aquí es que aMule no tiene ese hash. Por qué no lo tiene depende
    // de en qué punto esté la descarga, y decirlo mal es peor que no decirlo: este mismo
    // texto llegó a explicar que la película «ya había terminado» sobre una que iba por el
    // 9 % — y lo que había pasado era que aMule la había perdido.
    const terminada = !PENDIENTES.includes(estado);
    return (
      <Alert severity={terminada ? 'info' : 'warning'} sx={{ my: 1 }}>
        {terminada
          ? 'aMule ya no tiene esta descarga en su cola. Es lo normal en cuanto la película ' +
            'termina y se limpia: el fichero sigue donde esté, esto solo deja de tener ' +
            'detalle que contar.'
          : 'aMule no tiene esta descarga en su cola y por aquí todavía figura sin terminar. ' +
            'Puede que terminara sin que nos enteráramos —el porcentaje de arriba se queda ' +
            'clavado cuando eso pasa— o que aMule la perdiera a medias. Se decide mirando si ' +
            'el fichero está en su carpeta, y el estado de la descarga lo dirá en cuanto se ' +
            'resuelva.'}
      </Alert>
    );
  }
  return <Contenido d={data.detalle} downloadId={downloadId} viva={viva} />;
}

/** En estos estados la descarga todavía tenía que estar en la cola de aMule. */
const PENDIENTES: EstadoDescarga[] = ['queued', 'downloading', 'paused', 'hashing'];
